<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WahaSessionModal from './components/WahaSessionModal.vue';
import WahaSessionConfigModal from './components/WahaSessionConfigModal.vue';

const MODAL_TYPES = {
  CREATE: 'create',
  CONFIG: 'config',
  QR_CODE: 'qr_code',
};

const store = useStore();
const { t } = useI18n();

const wahaSessions = useMapGetter('wahaSessions/getSessions');
const uiFlags = useMapGetter('wahaSessions/getUIFlags');

const selectedSession = ref({});
const loading = ref({});
const modalType = ref(MODAL_TYPES.CREATE);
const wahaSessionModalRef = ref(null);
const wahaSessionConfigModalRef = ref(null);
const wahaSessionQrModalRef = ref(null);

const tableHeaders = computed(() => {
  return [
    t('WAHA_SESSIONS.LIST.TABLE_HEADER.NAME'),
    t('WAHA_SESSIONS.LIST.TABLE_HEADER.PHONE'),
    t('WAHA_SESSIONS.LIST.TABLE_HEADER.STATUS'),
    t('WAHA_SESSIONS.LIST.TABLE_HEADER.ACTIONS'),
  ];
});

const openCreateModal = () => {
  modalType.value = MODAL_TYPES.CREATE;
  selectedSession.value = {};
  wahaSessionModalRef.value.dialogRef.open();
};

const openConfigModal = session => {
  selectedSession.value = session;
  wahaSessionConfigModalRef.value.dialogRef.open();
};

const openQrCodeModal = session => {
  modalType.value = MODAL_TYPES.QR_CODE;
  selectedSession.value = session;
  wahaSessionQrModalRef.value.dialogRef.open();
};

const getStatusBadgeClass = status => {
  const baseClasses = 'inline-block px-2 py-0.5 text-xs rounded-md';
  const statusClasses = {
    WORKING: 'bg-n-green-5 text-n-green-11',
    SCAN_QR_CODE: 'bg-n-yellow-5 text-n-yellow-11',
    STARTING: 'bg-n-yellow-5 text-n-yellow-11',
    FAILED: 'bg-n-red-5 text-n-red-11',
    STOPPED: 'bg-n-slate-5 text-n-slate-11',
  };
  return `${baseClasses} ${statusClasses[status] || statusClasses.STOPPED}`;
};

const getStatusIcon = status => {
  const icons = {
    WORKING: 'i-lucide-check-circle',
    SCAN_QR_CODE: 'i-lucide-qr-code',
    STARTING: 'i-lucide-loader',
    FAILED: 'i-lucide-alert-circle',
    STOPPED: 'i-lucide-stop-circle',
  };
  return icons[status] || icons.STOPPED;
};

const handleSessionAction = async (session, action) => {
  const sessionId = session.id;
  loading.value[sessionId] = true;
  try {
    await store.dispatch(`wahaSessions/${action}`, sessionId);
    useAlert(t(`WAHA_SESSIONS.${action.toUpperCase()}.SUCCESS_MESSAGE`));
    // Reload sessions after action completes
    await store.dispatch('wahaSessions/get');
  } catch (error) {
    useAlert(t(`WAHA_SESSIONS.${action.toUpperCase()}.ERROR_MESSAGE`));
  } finally {
    // Use setTimeout to ensure UI updates smoothly after reload
    setTimeout(() => {
      loading.value[sessionId] = false;
    }, 100);
  }
};

const startSession = session => handleSessionAction(session, 'start');
const stopSession = session => handleSessionAction(session, 'stop');
const restartSession = session => handleSessionAction(session, 'restart');
const logoutSession = session => handleSessionAction(session, 'logout');

const syncSessions = async () => {
  try {
    await store.dispatch('wahaSessions/sync');
    useAlert(t('WAHA_SESSIONS.SYNC.SUCCESS_MESSAGE'));
    await store.dispatch('wahaSessions/get');
  } catch (error) {
    useAlert(t('WAHA_SESSIONS.SYNC.ERROR_MESSAGE'));
  }
};

onMounted(() => {
  store.dispatch('wahaSessions/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('WAHA_SESSIONS.LIST.LOADING')"
    :no-records-found="!wahaSessions.length"
    :no-records-message="t('WAHA_SESSIONS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('WAHA_SESSIONS.HEADER')"
        :description="t('WAHA_SESSIONS.DESCRIPTION')"
        :link-text="t('WAHA_SESSIONS.LEARN_MORE')"
        feature-name="waha_sessions"
      >
        <template #actions>
          <div class="flex gap-2">
            <Button
              icon="i-lucide-refresh-cw"
              :label="$t('WAHA_SESSIONS.SYNC.BUTTON_TEXT')"
              slate
              @click="syncSessions"
            />
            <Button
              icon="i-lucide-circle-plus"
              :label="$t('WAHA_SESSIONS.ADD.TITLE')"
              @click="openCreateModal"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-strong">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="session in wahaSessions" :key="session.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-col">
                <span class="font-medium">{{ session.name }}</span>
                <span
                  v-if="session.last_seen_at"
                  class="text-xs text-n-slate-11"
                >
                  {{ $t('WAHA_SESSIONS.LIST.LAST_SEEN') }}:
                  {{ new Date(session.last_seen_at).toLocaleString() }}
                </span>
              </div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-sm">
              <span v-if="session.phone_number">{{
                session.phone_number
              }}</span>
              <span v-else class="text-n-slate-11">-</span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span :class="getStatusBadgeClass(session.status)">
                <span
                  :class="getStatusIcon(session.status)"
                  class="inline-block w-3 h-3 mr-1"
                />
                {{ session.status }}
              </span>
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-if="session.stopped"
                  v-tooltip.top="t('WAHA_SESSIONS.START.BUTTON_TEXT')"
                  icon="i-lucide-play"
                  xs
                  emerald
                  faded
                  :is-loading="loading[session.id]"
                  @click="startSession(session)"
                />
                <Button
                  v-if="session.working"
                  v-tooltip.top="t('WAHA_SESSIONS.STOP.BUTTON_TEXT')"
                  icon="i-lucide-stop-circle"
                  xs
                  amber
                  faded
                  :is-loading="loading[session.id]"
                  @click="stopSession(session)"
                />
                <Button
                  v-if="session.working || session.failed"
                  v-tooltip.top="t('WAHA_SESSIONS.RESTART.BUTTON_TEXT')"
                  icon="i-lucide-refresh-cw"
                  xs
                  blue
                  faded
                  :is-loading="loading[session.id]"
                  @click="restartSession(session)"
                />
                <Button
                  v-if="session.status === 'SCAN_QR_CODE'"
                  v-tooltip.top="t('WAHA_SESSIONS.QR.SHOW_QR_CODE')"
                  icon="i-lucide-qr-code"
                  xs
                  violet
                  faded
                  :is-loading="loading[session.id]"
                  @click="openQrCodeModal(session)"
                />
                <Button
                  v-if="session.working"
                  v-tooltip.top="t('WAHA_SESSIONS.CONFIG.BUTTON_TEXT')"
                  icon="i-lucide-settings"
                  xs
                  slate
                  faded
                  :is-loading="loading[session.id]"
                  @click="openConfigModal(session)"
                />
                <Button
                  v-if="session.working"
                  v-tooltip.top="t('WAHA_SESSIONS.LOGOUT.BUTTON_TEXT')"
                  icon="i-lucide-log-out"
                  xs
                  orange
                  faded
                  :is-loading="loading[session.id]"
                  @click="logoutSession(session)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <WahaSessionModal
      ref="wahaSessionModalRef"
      :type="modalType"
      :selected-session="selectedSession"
    />

    <WahaSessionModal
      ref="wahaSessionQrModalRef"
      type="qr_code"
      :selected-session="selectedSession"
    />

    <WahaSessionConfigModal
      ref="wahaSessionConfigModalRef"
      :session="selectedSession"
    />
  </SettingsLayout>
</template>
